// EmptyStateHeroV3 — first-event onboarding centerpiece.
//
// Operator just created a source. They land on this page. The data
// table would be empty — instead of a sad "No rows match" hint, we
// show a centered hero with the THREE clearest paths to first event:
//
//   1. Copy the webhook URL → paste into a tool's webhook config
//   2. Drop a file (CSV / JSON / XLSX) → bulk ingest right here
//   3. Run a curl from the terminal → we generate the snippet
//
// Replaces the empty data table when source has zero events. As soon
// as the first event arrives, the parent page swaps this hero for
// DataSurfaceV3.

import React, { useState } from 'react';
import type { Source } from '@datahub/shared';
import { useToast } from '../Toast';

export function EmptyStateHeroV3({ source, onTestSent }: {
  source: Source;
  onTestSent: () => void;
}): React.ReactElement {
  const toast = useToast();
  const [sending, setSending] = useState(false);

  const webhookUrl = source.inputs.find(i => i.kind === 'webhook')?.url ?? '';
  const curlSnippet = `curl -X POST '${webhookUrl}' \\
  -H 'Content-Type: application/json' \\
  -d '{"event":"hello","value":42}'`;

  function copy(text: string, label: string): void {
    void navigator.clipboard.writeText(text);
    toast.show(`${label} copied`, { kind: 'success' });
  }

  async function sendTest(): Promise<void> {
    if (!webhookUrl) return;
    setSending(true);
    try {
      const r = await fetch(webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event: 'test.hello',
          message: 'Sent from the empty-state hero',
          ts: new Date().toISOString(),
        }),
      });
      if (r.ok || r.status === 202) {
        toast.show('Test event sent — your data will appear here in a moment', { kind: 'success' });
        onTestSent();
      } else {
        toast.show(`Test send failed (${r.status})`, { kind: 'error' });
      }
    } catch (e) {
      toast.show(`Test send failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={{
      padding: '32px 28px',
      background: 'var(--bg-elev)',
      border: '1px solid var(--line)',
      borderRadius: 10,
      display: 'flex', flexDirection: 'column', gap: 22,
      alignItems: 'center',
    }}>
      <div style={{ textAlign: 'center', maxWidth: 560 }}>
        <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', marginBottom: 6 }}>
          Source ready · waiting for the first event
        </div>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: 'var(--ink)' }}>
          Send any data here.
        </h2>
        <p style={{ margin: '8px 0 0', fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>
          Three ways to start. The data lands in BigQuery instantly via the auto-created <code style={{ fontFamily: 'var(--mono)' }}>events</code> view — no schema setup, no plumbing.
        </p>
      </div>

      {/* Webhook URL gets the full width — it's the primary action.
          Drop + curl sit below as secondary affordances on a wider grid. */}
      <div style={{ width: '100%', maxWidth: 880, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Option 1 — Webhook URL (full width, primary CTA) */}
        <div style={cardStyle}>
          <div style={cardLabelStyle}>1 · Webhook URL</div>
          <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>
            Paste this URL into any tool that supports outbound webhooks (Stripe, Pipedrive, Shopify, Zapier, …).
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <input
              readOnly
              value={webhookUrl}
              onClick={e => (e.target as HTMLInputElement).select()}
              style={{
                flex: 1, padding: '8px 10px', fontFamily: 'var(--mono)', fontSize: 12.5,
                border: '1px solid var(--line)', borderRadius: 4, background: 'var(--bg)', color: 'var(--ink)',
                minWidth: 0,
              }}
            />
            <button className="btn sm ghost" onClick={() => copy(webhookUrl, 'URL')} style={{ padding: '4px 12px', fontSize: 12 }}>copy</button>
            <button
              className="btn sm primary"
              onClick={() => { void sendTest(); }}
              disabled={sending}
              style={{ padding: '4px 14px', fontSize: 12 }}
            >{sending ? 'Sending…' : 'Send test →'}</button>
          </div>
        </div>

        {/* Options 2 + 3 side-by-side at viewport ≥ 720px, stacked otherwise */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12,
        }}>
          <div style={cardStyle}>
            <div style={cardLabelStyle}>2 · Drop a file</div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>
              Drag any <strong>CSV · JSON · NDJSON · XLSX · XML · TSV</strong> file anywhere on this page. Each row becomes an event.
            </div>
            <div style={{
              marginTop: 8,
              padding: '14px 12px',
              border: '2px dashed var(--line)',
              borderRadius: 6,
              background: 'var(--bg-sunken)',
              textAlign: 'center',
              fontSize: 12, color: 'var(--ink-3)',
              fontStyle: 'italic',
            }}>
              ↓ drop anywhere on the page ↓
            </div>
          </div>

          <div style={cardStyle}>
            <div style={cardLabelStyle}>3 · curl from terminal</div>
            <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>
              Try it right now from a terminal — paste the snippet, press Enter.
            </div>
            <pre style={{
              margin: '8px 0 0', padding: '10px 12px',
              background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 4,
              fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-2)',
              whiteSpace: 'pre', overflowX: 'auto',
            }}>{curlSnippet}</pre>
            <button
              className="btn sm ghost"
              onClick={() => copy(curlSnippet, 'curl snippet')}
              style={{ marginTop: 8, padding: '4px 10px', fontSize: 11, alignSelf: 'flex-start' }}
            >copy</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const cardStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column',
  padding: 14,
  background: 'var(--bg)',
  border: '1px solid var(--line)',
  borderRadius: 8,
};

const cardLabelStyle: React.CSSProperties = {
  fontSize: 10.5, fontFamily: 'var(--mono)',
  textTransform: 'uppercase', letterSpacing: '0.08em',
  color: 'var(--ink-3)',
  marginBottom: 6,
};
